import { Injectable, inject } from '@angular/core';
import { CollectionRequestBuilder, NavigatorService, Route } from '@web/common';
import {
  ConfirmationService,
  TableDataPayload,
  TableLazyLoad,
  ToastService,
} from '@web/shared/ui';
import { AdminBoxApiService } from '../api/services/admin-box-api.service';
import { map, Observable, tap } from 'rxjs';
import {
  executeOrNull,
  Guid,
  isEmptyArray,
  once,
  valueOrDefault,
} from '@web/utils';
import {
  AdminBoxAttributes,
  AdminBoxCharacteristics,
  AdminBoxDetailsForm,
} from '../models/admin-box-details-form';
import { AdminBoxCreateRequest } from '../api/requests/admin-box-create.request';
import { AdminBoxFileRequest } from '../api/requests/admin-box-file.request';
import {
  AdminMultimediaImageUpload,
  multimediaFromResponse,
} from '@web/shared/module/multimedia';
import { AdminBoxUpdateRequest } from '../api/requests/admin-box-update.request';
import { AdminBoxDetails } from '../models/admin-box-details';
import { AdminBoxCollectionFilter } from '../models/admin-box-collection-filter';
import { AdminBoxCollectionItem } from '../models/admin-box-collection-item';
import { AdminBoxAttributeDetails } from '../models/admin-box-attribute-details';
import { AdminBoxCharacteristicDetails } from '../models/admin-box-characteristic-details';
import { AdminBoxCosmetic } from '../models/admin-box-cosmetic';
import { AdminDictionaryProductAttributeRequest } from '@web/shared/module/dictionary';
import { AdminBoxCharacteristicRequest } from '../api/requests/admin-box-characteristic.request';
import { AdminBoxCosmeticRequest } from '../api/requests/admin-box-cosmetic.request';
import { AdminBoxQuantityHistoryCollectionFilter } from '../models/admin-box-quantity-history-collection-filter';
import { AdminBoxQuantityHistoryCollectionItem } from '../models/admin-box-quantity-history-collection-item';
import { AdminBoxQuantityHistoryOrder } from '../models/admin-box-quantity-history-order';

@Injectable({
  providedIn: 'root',
})
export class AdminBoxDataSourceService {
  private readonly adminBoxApi = inject(AdminBoxApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly confirmation = inject(ConfirmationService);
  private readonly toast = inject(ToastService);
  private readonly navigator = inject(NavigatorService);

  public fetchCollection(
    lazyLoad: TableLazyLoad<AdminBoxCollectionFilter>,
  ): Observable<TableDataPayload<AdminBoxCollectionItem>> {
    /* prettier-ignore */
    return this.adminBoxApi
      .fetchCollection(
        this.collectionRequestBuilder
          .page(lazyLoad.page)
          .pageSize(lazyLoad.rows)
          .sorts((builder) =>
            builder.fromTableSort(lazyLoad.sort).buildAsArray(),
          )
          .filters((builder) =>
            builder
              .fromTableFilter(lazyLoad.filter)
              .appendDate((builder) => builder.rangeFor('createdAt').build())
              .appendText((builder) => builder.equalFor('status').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('name').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('slug').build())
              .buildAsArray(),
          )
          .build(),
      )
      .pipe(
        map(
          (response) =>
            new TableDataPayload(
              response.items.map(
                (item) =>
                  /* prettier-ignore */
                  new AdminBoxCollectionItem({
                    boxSaleId: item.boxSale?.id,
                    canBeBoxSaleCreated: item.contextOperations.canBeBoxSaleCreated,
                    canBeBoxSaleViewed: item.contextOperations.canBeBoxSaleViewed,
                    canBeOrdersCompleted: item.contextOperations.canBeOrdersCompleted,
                    canBeOrdersCompletedSimulated: item.contextOperations.canBeOrdersCompletedSimulated,
                    canDownloadLabels: item.contextOperations.canDownloadLabels,
                    canBeEdited: item.contextOperations.canBeEdited,
                    canBeRemoved: item.contextOperations.canBeDeleted,
                    canBeViewed: item.contextOperations.canBeViewed,
                    createdAt: item.createdAt,
                    featuredFileAlt: item.featuredFile?.alt(),
                    featuredFileUrl: item.featuredFile?.url,
                    id: item.id,
                    name: item.name,
                    price: item.price,
                    availableQuantity: item.availableQuantity,
                    slug: item.slug,
                    status: item.status,
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  public fetchQuantityHistoryCollection(
    id: Guid,
    lazyLoad: TableLazyLoad<AdminBoxQuantityHistoryCollectionFilter>,
  ): Observable<TableDataPayload<AdminBoxQuantityHistoryCollectionItem>> {
    return this.adminBoxApi
      .fetchQuantityHistoryCollection({
        request: this.collectionRequestBuilder
          .page(lazyLoad.page)
          .pageSize(lazyLoad.rows)
          .sorts((builder) =>
            builder.fromTableSort(lazyLoad.sort).buildAsArray(),
          )
          .filters((builder) =>
            builder
              .fromTableFilter(lazyLoad.filter)
              .appendDate((builder) => builder.rangeFor('createdAt').build())
              .appendText((builder) => builder.equalFor('change').build())
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('reason').build(),
              )
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('createdBy').build(),
              )
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('order').build(),
              )
              .buildAsArray(),
          )
          .build(),
        id,
      })
      .pipe(
        map(
          (response) =>
            new TableDataPayload<AdminBoxQuantityHistoryCollectionItem>(
              response.items.map(
                (item) =>
                  new AdminBoxQuantityHistoryCollectionItem({
                    change: item.change,
                    createdAt: item.createdAt,
                    createdBy: item.createdBy,
                    id: item.id,
                    order: executeOrNull(
                      item.order,
                      (order) =>
                        new AdminBoxQuantityHistoryOrder({
                          id: order.id,
                          number: order.number,
                        }),
                    ),
                    quantityAfterChange: item.quantityAfterChange,
                    quantityBeforeChange: item.quantityBeforeChange,
                    reason: item.reason,
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  /**
   * @deprecated
   */
  public removeById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz usunąć wybrany box kosmetyczny?')
          .accept(() => {
            this.adminBoxApi
              .removeById(id)
              .pipe(
                once(),
                tap(() => {
                  this.toast.show((builder) =>
                    builder
                      .initSuccess(
                        'Wybrany box kosmetyczny został usunięty pomyślnie.',
                      )
                      .build(),
                  );
                }),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public createByFormData(data: AdminBoxDetailsForm): Observable<void> {
    return this.adminBoxApi.create(
      new AdminBoxCreateRequest({
        name: data.name,
        featuredFileId: data.featuredFile?.id,
        illustrationFileId: data.illustrationFile?.id,
        symbolFileId: data.symbolFile?.id,
        status: data.status,
        password: data.password,
        price: valueOrDefault(
          executeOrNull(data.price, (price) => String(price)),
          '',
        ),
        cosmeticBrandId: data.cosmeticBrandId,
        boxCategoryId: data.boxCategoryId,
        warehouseGoodId: data.warehouseProductId,
        cosmetics: valueOrDefault(data.cosmetics, []).map(
          (cosmetic) => new AdminBoxCosmeticRequest({ id: cosmetic.id }),
        ),
        availableQuantity: data.availableQuantity,
        maxQuantityPerOrder: data.maxQuantityPerOrder,
        contentPreviewUrl: data.contentPreviewUrl,
        currentEdition: data.currentEdition,
        attributes: data.attributes?.items.map(
          (attribute) =>
            new AdminDictionaryProductAttributeRequest({
              productAttributeId: attribute.productAttributeId!,
              value: attribute.content,
            }),
        ),
        characteristics: data.characteristics?.items.map(
          (characteristic) =>
            new AdminBoxCharacteristicRequest({
              label: characteristic.label,
              value: characteristic.content,
            }),
        ),
        files: valueOrDefault(
          executeOrNull(data.files, (files) =>
            files.map(({ id }) => new AdminBoxFileRequest({ id })),
          ),
          [],
        ),
      }),
    );
  }

  /* prettier-ignore */
  public fetchDetailsFormById(
    id: string,
  ): Observable<AdminBoxDetailsForm> {
    return this.adminBoxApi.fetchDetailsById(id).pipe(
      map(
        (response) =>
          new AdminBoxDetailsForm({
            name: response.name,
            password: response.password,
            price: response.price,
            slug: response.slug,
            status: response.status,
            cosmeticBrandId: response.cosmeticBrand?.id,
            boxCategoryId: response.category?.id,
            cosmetics: valueOrDefault(
              response.cosmetics?.map(
                (cosmetic) =>
                  new AdminBoxCosmetic({
                    cosmeticBrandName: cosmetic.cosmeticBrand?.name,
                    featureFileAlt: cosmetic.featureFile?.alt(),
                    featureFileUrl: cosmetic.featureFile?.url,
                    id: cosmetic.id,
                    name: cosmetic.name,
                  }),
              ),
              [],
            ),
            availableQuantity: response.availableQuantity,
            warehouseProductId: response.warehouseGood?.id,
            canBeWarehouseProductIdEdited:
              response.contextOperations.canBeWarehouseGoodIdEdited,
            maxQuantityPerOrder: response.maxQuantityPerOrder,
            contentPreviewUrl: response.contentPreviewUrl,
            currentEdition: response.currentEdition,
            publishedAt: response.publishedAt,
            symbolFile: executeOrNull(response.symbolFile?.id, (id) => new AdminMultimediaImageUpload({ id })),
            featuredFile: executeOrNull(response.featuredFile?.id, (id) => new AdminMultimediaImageUpload({ id })),
            illustrationFile: executeOrNull(response.illustrationFile?.id, (id) => new AdminMultimediaImageUpload({ id })),
            attributes: executeOrNull(
              response.attributes,
              (attributes) => new AdminBoxAttributes({
                items: attributes.map(({ value, id, productAttribute }) => ({
                  productAttributeId: productAttribute.id,
                  id,
                  content: value,
                })),
              }),
            ),
            characteristics: executeOrNull(
              response.characteristics,
              (characteristics) => new AdminBoxCharacteristics({
                items: characteristics.map(({ label, id, value }) => ({
                  label,
                  id,
                  content: value,
                })),
              }),
            ),
            files: executeOrNull(response.files, (files) => {
              if (isEmptyArray(files)) {
                return null;
              }

              return files.map(({ id }) => new AdminMultimediaImageUpload({ id }));
            }),
            id: response.id,
          }),
      ),
    );
  }

  public createSaleById(params: { id: Guid; name: string }): Observable<void> {
    return this.adminBoxApi.createSale(params.id).pipe(
      tap(() => {
        this.toast.show((builder) =>
          builder
            .initSuccess(
              'Konfiguracja sprzedaży wybranego boxa kosmetycznego została utworzona.',
            )
            .build(),
        );
        this.navigator.navigateByRouterLink(
          (provider) => provider.routerLink(Route.BoxSales),
        );
      }),
    );
  }

  public updateByFormData(
    id: Guid,
    data: AdminBoxDetailsForm,
  ): Observable<void> {
    return this.adminBoxApi.update(
      id,
      new AdminBoxUpdateRequest({
        name: data.name,
        illustrationFileId: valueOrDefault(data.illustrationFile?.id, null),
        symbolFileId: valueOrDefault(data.symbolFile?.id, null),
        featuredFileId: valueOrDefault(data.featuredFile?.id, null),
        status: data.status,
        files: valueOrDefault(
          executeOrNull(data.files, (files) =>
            files.map(({ id }) => new AdminBoxFileRequest({ id })),
          ),
          [],
        ),
        password: data.password,
        price: valueOrDefault(
          executeOrNull(data.price, (price) => String(price)),
          '',
        ),
        cosmeticBrandId: data.cosmeticBrandId,
        boxCategoryId: data.boxCategoryId,
        warehouseGoodId: data.warehouseProductId,
        cosmetics: valueOrDefault(data.cosmetics, []).map(
          (cosmetic) => new AdminBoxCosmeticRequest({ id: cosmetic.id }),
        ),
        availableQuantity: data.availableQuantity,
        maxQuantityPerOrder: data.maxQuantityPerOrder,
        contentPreviewUrl: data.contentPreviewUrl,
        currentEdition: data.currentEdition,
        publishedAt: data.publishedAt,
        slug: valueOrDefault(data.slug, ''),
        attributes: data.attributes?.items.map(
          (attribute) =>
            new AdminDictionaryProductAttributeRequest({
              id: attribute.id,
              productAttributeId: attribute.productAttributeId!,
              value: attribute.content,
            }),
        ),
        characteristics: data.characteristics?.items.map(
          (characteristic) =>
            new AdminBoxCharacteristicRequest({
              id: characteristic.id,
              label: characteristic.label,
              value: characteristic.content,
            }),
        ),
      }),
    );
  }

  public fetchDetailsById(id: Guid): Observable<AdminBoxDetails> {
    return this.adminBoxApi.fetchDetailsById(id).pipe(
      map(
        (response) =>
          new AdminBoxDetails({
            canBeEdited: response.contextOperations.canBeEdited,
            canBeRemoved: response.contextOperations.canBeDeleted,
            canBeViewed: response.contextOperations.canBeViewed,
            cosmeticBrandName: response.cosmeticBrand?.name,
            symbolFile: executeOrNull(response.symbolFile, (file) =>
              multimediaFromResponse(file),
            ),
            illustrationFile: executeOrNull(response.illustrationFile, (file) =>
              multimediaFromResponse(file),
            ),
            createdAt: response.createdAt,
            publishedAt: response.publishedAt,
            featuredFile: executeOrNull(response.featuredFile, (file) =>
              multimediaFromResponse(file),
            ),
            id: response.id,
            name: response.name,
            password: response.password,
            price: response.price,
            slug: response.slug,
            status: response.status,
            boxCategoryName: response.category?.label,
            cosmetics: valueOrDefault(
              response.cosmetics?.map(
                (cosmetic) =>
                  new AdminBoxCosmetic({
                    cosmeticBrandName: cosmetic.cosmeticBrand?.name,
                    featureFileAlt: cosmetic.featureFile?.alt(),
                    featureFileUrl: cosmetic.featureFile?.url,
                    id: cosmetic.id,
                    name: cosmetic.name,
                  }),
              ),
              [],
            ),
            availableQuantity: response.availableQuantity,
            warehouseProductName: response.warehouseGood?.name,
            canBeWarehouseProductIdEdited:
              response.contextOperations.canBeWarehouseGoodIdEdited,
            maxQuantityPerOrder: response.maxQuantityPerOrder,
            contentPreviewUrl: response.contentPreviewUrl,
            currentEdition: response.currentEdition,
            files: valueOrDefault(
              executeOrNull(response.files, (files) =>
                files.map((file) => multimediaFromResponse(file)),
              ),
              [],
            ),
            characteristics: valueOrDefault(
              executeOrNull(response.characteristics, (characteristics) =>
                characteristics.map(
                  (characteristic) =>
                    new AdminBoxCharacteristicDetails({
                      id: characteristic.id,
                      content: characteristic.value,
                      label: characteristic.label,
                    }),
                ),
              ),
              [],
            ),
            attributes: valueOrDefault(
              executeOrNull(response.attributes, (attributes) =>
                attributes.map(
                  (attribute) =>
                    new AdminBoxAttributeDetails({
                      id: attribute.id,
                      content: attribute.value,
                      label: attribute.productAttribute.label,
                    }),
                ),
              ),
              [],
            ),
          }),
      ),
    );
  }

  public completeOrdersById(id: Guid): Observable<Guid>{
    return this.adminBoxApi
      .completeOrders(id)
      .pipe(map((response) => response.id));
  }

  public simulateCompleteOrdersById(id: Guid): Observable<Guid>{
    return this.adminBoxApi
      .simulateCompleteOrders(id)
      .pipe(map((response) => response.id));
  }

  public downloadLabels(id: Guid): Observable<Guid>{
    return this.adminBoxApi
      .downloadLabels(id)
      .pipe(map((response) => response.id));
  }
}
